import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown, Download } from "lucide-react";
import Marquee from "react-fast-marquee";
import { useLang } from "../context/LanguageContext";
import { IMAGES } from "../data/assets";
import { usePresentation } from "./PresentationModal";

const easeOut = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { t } = useLang();
  const { openModal } = usePresentation();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0A0A0B]"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Oil rig operations"
          className="w-full h-full object-cover opacity-[0.55]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/70 to-[#0A0A0B]/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B]/90 via-[#0A0A0B]/40 to-transparent" />
        <div className="absolute inset-0 grid-overlay opacity-40" />
      </div>

      {/* Side decorative lines */}
      <div className="absolute top-[72px] left-6 lg:left-12 bottom-0 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent hidden md:block" />
      <div className="absolute top-[72px] right-6 lg:right-12 bottom-0 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent hidden md:block" />

      {/* Content */}
      <div className="relative z-10 min-h-[100svh] pt-[120px] pb-24 flex flex-col">
        <div className="max-w-[1400px] w-full mx-auto px-6 sm:px-8 lg:px-12 flex-1 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: easeOut, delay: 0.2 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="overline">{t.hero.eyebrow}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: easeOut, delay: 0.35 }}
            className="font-display font-black text-white tracking-[-0.02em] leading-[0.95] text-[clamp(2.5rem,7vw,6.5rem)] max-w-[18ch]"
          >
            {t.hero.title_1}
            <br />
            <span className="gold-gradient-text">{t.hero.title_2}</span>
            <br />
            <span className="text-[#F4F4F5]/90">{t.hero.title_3}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: easeOut, delay: 0.6 }}
            className="mt-8 max-w-2xl text-base lg:text-lg text-[#A1A1AA] leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: easeOut, delay: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollTo("contact")}
              className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-[12px] font-bold uppercase tracking-[0.2em] hover:bg-[#F0C84B] transition-all duration-300"
            >
              {t.hero.primary}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollTo("services")}
              className="group inline-flex items-center gap-3 border border-white/20 text-white px-7 py-4 text-[12px] font-bold uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
            >
              {t.hero.secondary}
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-deck-cta"
              onClick={openModal}
              className="group inline-flex items-center gap-3 text-[#D4AF37] px-3 py-4 text-[12px] font-bold uppercase tracking-[0.2em] hover:text-[#F0C84B] transition-all duration-300"
            >
              <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              {t.presentation.cta_short}
            </button>
          </motion.div>
        </div>

        {/* Bottom marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="border-t border-white/5 py-6 bg-[#0A0A0B]/60 backdrop-blur"
        >
          <Marquee speed={50} gradient={false} pauseOnHover>
            <div className="flex items-center gap-8 px-6 text-[11px] uppercase tracking-[0.3em] text-[#8B939C]">
              {Array.from({ length: 6 }).map((_, i) => (
                <React.Fragment key={i}>
                  <span>{t.hero.marquee}</span>
                  <span className="w-1 h-1 bg-[#D4AF37] rounded-full" />
                </React.Fragment>
              ))}
            </div>
          </Marquee>
        </motion.div>
      </div>
    </section>
  );
}
